import React, { useContext, useState, useEffect, useRef, useLayoutEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/pro-solid-svg-icons"
import { FormattedMessage, useIntl } from "react-intl"
import { FirebaseContext } from "../../Firebase"
import DailyQuest from "../../daily-quest/dashboard-daily-quest"
import RoadmapFeaturedStories from "./RoadmapFeaturedStories"
import { RoadmapStepButton, RoadmapStepDescription } from "./RoadmapItems"
import { navigate } from "../../../link"
import {
  WingsButton,
  WingsBadge,
  WingsEyebrow,
  WingsHeading,
  WingsAccordion,
  WingsDropdown,
  WingsText,
  WingsDetailText,
  WingsCheckbox,
  WingsDisplay,
} from "@wingspans-ui"
import useSampleGoal from "../goals/useSampleGoal"
import "./Roadmap.css"

/* ────────────────────────────────────────────────────────────────────────────
 * Constants
 * ──────────────────────────────────────────────────────────────────────────── */

/** Fixed sequence of roadmap milestones shown in the Action Plan. */
const PLAN_STEPS = [
  {
    key: "daily-quest",
    titleId: "roadmap-step-daily-quest",
    defaultTitle: "Daily Quest",
    href: null,
    buttonLabelId: "roadmap-step-daily-quest-button",
    defaultButtonLabel: "Start Daily Quest",
    active: false,
  },
  {
    key: "assessment",
    titleId: "roadmap-step-assessment",
    defaultTitle: "Take an interest profiler",
    href: "/assessment/personality/",
    buttonLabelId: "roadmap-step-assessment-button",
    defaultButtonLabel: "Interest Profiler",
    active: false,
  },
  {
    key: "skills",
    titleId: "roadmap-step-skills",
    defaultTitle: "Translate your experiences to skills",
    href: "/tal-e/",
    buttonLabelId: "roadmap-step-skills-button",
    defaultButtonLabel: "TAL-E",
    active: false,
  },
  {
    key: "explore",
    titleId: "roadmap-step-explore",
    defaultTitle: "Explore people, careers, degrees, schools, and jobs",
    href: "/dashboard/?tab=explore",
    buttonLabelId: "roadmap-step-explore-button",
    defaultButtonLabel: "Explore by Category",
    active: false,
  },
  {
    key: "soft-skills",
    titleId: "roadmap-step-soft-skills",
    defaultTitle: "Develop and hone your soft skills",
    minutes: 35,
    href: "/dashboard/?tab=work-readiness",
    buttonLabelId: "roadmap-step-soft-skills-button",
    defaultButtonLabel: "Skill Builder",
    active: false,
  },
  {
    key: "resume",
    titleId: "roadmap-step-resume",
    defaultTitle: "Build a stand-out resume",
    href: "/dashboard/?tab=portfolio",
    buttonLabelId: "roadmap-step-resume-button",
    defaultButtonLabel: "Resume",
    active: false,
  },
  {
    key: "career-plan",
    titleId: "roadmap-step-career-plan",
    defaultTitle: "Create career plans",
    href: "/dashboard/?tab=career-plan",
    buttonLabelId: "roadmap-step-career-plans-button",
    defaultButtonLabel: "Career Plans",
    active: false,
  },
  {
    key: "goals",
    titleId: "roadmap-step-goals",
    defaultTitle: "Set goals and stay inspired",
    href: "/dashboard?tab=goal-tracking",
    buttonLabelId: "roadmap-step-goals-button",
    defaultButtonLabel: "Set Goals",
    active: false,
  },
]

/** Steps that never show a "Mark as complete" checkbox. */
const NON_COMPLETABLE_STEP_KEYS = new Set([
  "daily-quest",
  "explore",
  "soft-skills",
  "career-plan",
  "resume",
  "goals",
])

const ACTIVE_ASSIGNMENT_STATUSES = new Set(["assigned", "in_progress", "not_started"])
const SAMPLE_GOAL_ROADMAP_ID = "sample-goal"

/* ────────────────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────────────────── */

/** Goals from the profile that were explicitly linked to the roadmap. */
const getLinkedGoals = (goals) =>
  Array.isArray(goals)
    ? goals.filter((goal) => goal && typeof goal === "object" && goal.linked === true && goal.name)
    : []

const getAssignmentStatus = (assignment) => `${assignment?.status ?? ""}`.toLowerCase()

const isAssignmentCompleted = (assignment) => getAssignmentStatus(assignment) === "completed"

/** Assignments come in two flavors; resolve which one this is and its id. */
const getAssignmentTypeAndId = (assignment) => {
  const type =
    assignment?.assignment_type === "program" || assignment?.program_id ? "program" : "module"
  return { type, id: type === "program" ? assignment?.program_id : assignment?.module_id }
}

/** Format a goal's target date, tolerating both `YYYY-MM-DD` and ISO strings. */
const formatGoalTargetDate = (targetDate, locale) => {
  if (!targetDate) return null

  const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(targetDate)
    ? new Date(`${targetDate}T00:00:00`) // treat date-only strings as local time
    : new Date(targetDate)

  if (isNaN(parsedDate.getTime())) return null

  return new Intl.DateTimeFormat(locale || "en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(parsedDate)
}

/* ────────────────────────────────────────────────────────────────────────────
 * Plan item components
 * ──────────────────────────────────────────────────────────────────────────── */

/** A user goal (or the sample goal) linked into the Action Plan. */
const GoalPlanItem = ({ displayIndex, goal, onToggleTask, onToggleComplete }) => {
  const { locale } = useIntl()
  const targetDate = formatGoalTargetDate(goal?.targetDate, locale)
  const isCompleted = goal?.completed ?? false

  return (
    <WingsDropdown
      className="roadmap-step-dropdown roadmap-step-dropdown-goal w-mb-1"
      closedColor="var(--w-color-teal-400)"
      openColor="var(--w-color-teal-400)"
      fontColor="var(--w-color-gray-900)"
      title={(
        <span className="roadmap-step-dropdown-title">
          <span className="roadmap-step-number">
            {isCompleted ? <FontAwesomeIcon icon={faCheck} /> : displayIndex}
          </span>
          <span className="roadmap-step-title">
            <FormattedMessage
              id={isCompleted ? "roadmap-completed-goal" : "roadmap-complete-goal"}
              defaultMessage="Goal:"
            /> {goal.name}
          </span>
        </span>
      )}
    >
      {targetDate && (
        <WingsDetailText as="p" className="roadmap-linked-goal-target-date px-4 w-mb-1">
          <b><FormattedMessage id="goal-tracking-task-target-date" defaultMessage="Target Date:" /></b> {targetDate}
        </WingsDetailText>
      )}

      {goal.tasks?.length > 0 && (
        <ul className="roadmap-linked-goal-tasks px-4 w-mb-1">
          {goal.tasks.filter(Boolean).map((task, taskIndex) => (
            <li key={task.id ?? taskIndex} className="roadmap-linked-goal-task-item">
              <WingsCheckbox
                checked={task.completed ?? false}
                onChange={() => onToggleTask(goal, task.id)}
              />
              <WingsText
                as="span"
                role="button"
                tabIndex={0}
                onClick={() => onToggleTask(goal, task.id)}
                onKeyDown={(e) => { if (e.key === "Enter") onToggleTask(goal, task.id) }}
                className={`roadmap-linked-goal-task-name${task.completed ? " roadmap-linked-goal-task-name-done" : ""}`}
              >
                {task.name ?? task}
              </WingsText>
            </li>
          ))}
        </ul>
      )}

      <div className="px-4">
        <WingsButton
          variant="secondary"
          size="mini"
          onClick={() => navigate("/dashboard?tab=goal-tracking")}
        >
          <FormattedMessage id="roadmap-goal-tracker-button" defaultMessage="Goal Tracker" />
        </WingsButton>
      </div>

      <div className="roadmap-step-completion roadmap-linked-goal-completion">
        <WingsCheckbox
          checked={isCompleted}
          onChange={() => onToggleComplete(goal)}
          label={<FormattedMessage id="roadmap-mark-goal-complete" defaultMessage="Complete Goal" />}
          labelPosition="left"
          className={`roadmap-step-completion-label${isCompleted ? " roadmap-step-completion-label-done" : ""}`}
        />
      </div>
    </WingsDropdown>
  )
}

/** A curriculum assignment (program or module) surfaced in the Action Plan. */
const AssignmentPlanItem = ({ displayIndex, assignment }) => {
  const { locale, formatDate } = useIntl()
  const isCompleted = isAssignmentCompleted(assignment)
  const { type, id } = getAssignmentTypeAndId(assignment)

  const path = id
    ? `${locale === "es-ES" ? "/es-ES" : ""}/${type === "program" ? "programs" : "modules"}/${id}`
    : null
  const title = locale === "es-ES"
    ? (assignment?.title_es || assignment?.title || "")
    : (assignment?.title || assignment?.title_es || "")
  const dueDate = assignment?.due_at
    ? formatDate(new Date(assignment.due_at), { month: "short", day: "numeric", year: "numeric" })
    : null
  const assignedBy = [assignment?.creator_first_name, assignment?.creator_last_name]
    .filter(Boolean)
    .join(" ")

  return (
    <WingsDropdown
      className="roadmap-step-dropdown roadmap-step-dropdown-goal w-mb-1"
      closedColor="var(--w-color-orange-300)"
      openColor="var(--w-color-orange-300)"
      fontColor="var(--w-color-gray-900)"
      title={(
        <span className="roadmap-step-dropdown-title">
          <span className="roadmap-step-number">
            {isCompleted ? <FontAwesomeIcon icon={faCheck} /> : displayIndex}
          </span>
          <span className="roadmap-step-title">
            <FormattedMessage id="roadmap-complete-assignment" defaultMessage="Assignment:" /> {title}
          </span>
        </span>
      )}
    >
      <div className="px-4">
        {assignedBy && (
          <WingsDetailText as="p" className="w-mb-1">
            <b><FormattedMessage id="curriculum-assigned-by" defaultMessage="Assigned by:" /></b> {assignedBy}
          </WingsDetailText>
        )}
        {dueDate && (
          <WingsDetailText as="p" className="w-mb-1">
            <b><FormattedMessage id="curriculum-due-date" defaultMessage="Due Date:" /></b> {dueDate}
          </WingsDetailText>
        )}
        {path && (
          <WingsButton variant="primary" size="mini" onClick={() => navigate(path)}>
            <FormattedMessage
              id={type === "program" ? "roadmap-open-program-assignment" : "roadmap-open-module-assignment"}
              defaultMessage={type === "program" ? "Open Program" : "Open Module"}
            />
          </WingsButton>
        )}
      </div>
    </WingsDropdown>
  )
}

/** A fixed roadmap milestone from PLAN_STEPS. */
const StepPlanItem = ({ displayIndex, step, isComplete, canComplete, onToggleComplete }) => (
  <WingsDropdown
    className={`roadmap-step-dropdown w-mb-1${step.active ? " roadmap-step-dropdown-active" : ""}${step.key === "daily-quest" ? " roadmap-step-dropdown-daily-quest" : ""}`}
    defaultOpen={step.active}
    closedColor={step.key === "daily-quest" ? "var(--w-color-yellow-500)" : "var(--w-color-cyan-100)"}
    openColor={step.key === "daily-quest" ? "var(--w-color-yellow-500)" : "var(--w-color-cyan-100)"}
    fontColor="var(--w-color-gray-900)"
    title={(
      <span className="roadmap-step-dropdown-title">
        <span className="roadmap-step-number">
          {isComplete ? <FontAwesomeIcon icon={faCheck} /> : displayIndex}
        </span>
        <span className="roadmap-step-title">
          <FormattedMessage id={step.titleId} defaultMessage={step.defaultTitle} />
        </span>
      </span>
    )}
  >
    {step.key === "daily-quest"
      ? <DailyQuest />
      : <RoadmapStepDescription stepKey={step.key} isActive={step.active} />}

    <RoadmapStepButton
      href={step.href}
      labelId={step.buttonLabelId}
      defaultLabel={step.defaultButtonLabel}
    />

    {canComplete && (
      <div className="roadmap-step-completion roadmap-linked-goal-completion">
        <WingsCheckbox
          checked={isComplete}
          onChange={() => onToggleComplete(step.key)}
          label={<FormattedMessage id="roadmap-mark-complete" defaultMessage="Mark as complete" />}
          labelPosition="left"
          className={`roadmap-step-completion-label${isComplete ? " roadmap-step-completion-label-done" : ""}`}
        />
      </div>
    )}
  </WingsDropdown>
)

/* ────────────────────────────────────────────────────────────────────────────
 * Roadmap
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Roadmap dashboard homepage showing the user's goal, action plan, featured
 * stories, and recommended careers.
 */
const Roadmap = () => {
  const { firebase, user, profile } = useContext(FirebaseContext)
  const { formatDate, locale } = useIntl()
  const {
    goal: sampleGoal,
    loading: sampleGoalLoading,
    saving: sampleGoalSaving,
    toggleStepComplete: toggleSampleGoalStepComplete,
    setComplete: setSampleGoalComplete,
  } = useSampleGoal()

  /* ── State ── */

  const [careerPlan, setCareerPlan] = useState(null)
  const [linkedGoals, setLinkedGoals] = useState([])
  const [curriculumAssignments, setCurriculumAssignments] = useState([])
  const [stepsCompleted, setStepsCompleted] = useState({})
  const [filterTab, setFilterTab] = useState("active")

  // Completion state "snapshots", captured when data first loads and re-captured
  // on every tab switch. The Active/Completed filters compare against these
  // rather than live values, so checking an item off doesn't instantly yank it
  // out of the current list — it only moves on the next tab change.
  const [goalCompletionSnapshot, setGoalCompletionSnapshot] = useState({})
  const [stepCompletionSnapshot, setStepCompletionSnapshot] = useState({})

  const greetingRef = useRef(null)

  /* ── Data loading & sync ── */

  // Most recently created career plan doubles as the user's headline goal.
  useEffect(() => {
    if (!firebase || !user) return

    const fetchCareerPlan = async () => {
      try {
        const plans = await firebase.loadCareerPlans(user.uid)
        if (plans?.length > 0) {
          const sorted = [...plans].sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt) : 0
            const dateB = b.created ? new Date(b.updatedAt) : 0
            return dateB - dateA
          })
          setCareerPlan(sorted[0])
        }
      } catch (err) {
        console.error("Roadmap: error loading career plans", err)
      }
    }

    fetchCareerPlan()
  }, [firebase, user])

  useEffect(() => {
    if (!firebase) return

    const fetchAssignments = async () => {
      try {
        const res = await firebase.getModulesAssignedToUser()
        const programAssignments = Array.isArray(res?.data?.programAssignments) ? res.data.programAssignments : []
        const moduleAssignments = Array.isArray(res?.data?.moduleAssignments) ? res.data.moduleAssignments : []
        setCurriculumAssignments([...programAssignments, ...moduleAssignments])
      } catch (err) {
        console.error("Roadmap: error loading curriculum assignments", err)
        setCurriculumAssignments([])
      }
    }

    fetchAssignments()
  }, [firebase])

  // Mirror linked goals locally (for optimistic updates) and seed the goal
  // completion snapshot for any goals we haven't seen yet.
  useEffect(() => {
    const goals = getLinkedGoals(profile?.goals)
    setLinkedGoals(goals)

    setGoalCompletionSnapshot((prev) => {
      const next = { ...prev }
      goals.forEach((goal) => {
        if (next[goal.id] === undefined) next[goal.id] = Boolean(goal?.completed)
      })
      return next
    })
  }, [profile?.goals])

  // Mirror step completion locally and seed the step completion snapshot.
  useEffect(() => {
    const fromProfile =
      typeof profile?.stepsCompleted === "object" && profile?.stepsCompleted !== null
        ? profile.stepsCompleted
        : {}
    setStepsCompleted(fromProfile)

    setStepCompletionSnapshot((prev) => {
      const next = { ...prev }
      PLAN_STEPS.forEach((step) => {
        if (NON_COMPLETABLE_STEP_KEYS.has(step.key) || next[step.key] !== undefined) return
        next[step.key] = Boolean(fromProfile[step.key])
      })
      return next
    })
  }, [profile?.stepsCompleted])

  /* ── Greeting sizing ── */

  // Text width scales linearly with font size, so measure it once at a reference
  // size, then derive the font size from the container width (no fit loop).
  useLayoutEffect(() => {
    const el = greetingRef.current
    const container = el?.parentElement
    if (!el || !container) return

    const REFERENCE_SIZE = 100
    const DESKTOP_MAX_SIZE = 73
    const MOBILE_MAX_SIZE = 88
    const SAFETY_FACTOR = 0.97
    let ratio = 0
    let lastWidth = -1

    const applySize = () => {
      const width = container.clientWidth
      if (!ratio || width === lastWidth) return
      lastWidth = width

      const maxSize = window.matchMedia("(max-width: 760px)").matches ? MOBILE_MAX_SIZE : DESKTOP_MAX_SIZE
      el.style.fontSize = `${Math.min(maxSize, Math.floor((width / ratio) * SAFETY_FACTOR))}px`
    }

    const measureRatio = () => {
      el.style.fontSize = `${REFERENCE_SIZE}px`
      // The CSS max-width: 100% would otherwise clamp the measured width.
      el.style.width = "max-content"
      el.style.maxWidth = "none"
      const naturalWidth = el.getBoundingClientRect().width
      el.style.width = ""
      el.style.maxWidth = ""

      ratio = naturalWidth / REFERENCE_SIZE
      lastWidth = -1
      applySize()
    }

    measureRatio()

    // Web font swaps change glyph widths after first paint.
    document.fonts?.ready.then(measureRatio)

    const observer = new ResizeObserver(applySize)
    observer.observe(container)
    return () => observer.disconnect()
  }, [profile?.firstName, locale])

  /* ── Handlers ── */

  const handleToggleGoalTask = async (goal, taskId) => {
    // The sample goal persists through its own hook, not the profile.
    if (goal?.isSampleGoal) {
      const task = goal?.tasks?.find((t) => t?.id === taskId)
      await toggleSampleGoalStepComplete(taskId, !task?.completed)
      return
    }

    if (!Array.isArray(profile?.goals)) return

    const updatedGoals = profile.goals.map((profileGoal) => {
      if (profileGoal?.id !== goal?.id) return profileGoal
      const updatedTasks = (profileGoal.tasks ?? []).map((task) =>
        task?.id === taskId ? { ...task, completed: !task.completed } : task
      )
      return { ...profileGoal, tasks: updatedTasks }
    })

    setLinkedGoals(getLinkedGoals(updatedGoals)) // optimistic update
    firebase.updateProfile({ data: { goals: updatedGoals } })
  }

  const handleToggleGoalComplete = async (goal) => {
    if (goal?.isSampleGoal) {
      await setSampleGoalComplete(!goal?.completed)
      return
    }

    if (!Array.isArray(profile?.goals)) return

    const updatedGoals = profile.goals.map((profileGoal) =>
      profileGoal?.id === goal?.id
        ? { ...profileGoal, completed: !profileGoal.completed }
        : profileGoal
    )

    setLinkedGoals(getLinkedGoals(updatedGoals)) // optimistic update
    firebase.updateProfile({ data: { goals: updatedGoals } })
  }

  const handleToggleStepComplete = (stepKey) => {
    if (NON_COMPLETABLE_STEP_KEYS.has(stepKey)) return

    const updated = { ...stepsCompleted, [stepKey]: !stepsCompleted[stepKey] }
    setStepsCompleted(updated) // optimistic update
    firebase.updateProfile({ data: { stepsCompleted: updated } })
  }

  // Re-snapshot completion state so the new tab reflects everything toggled
  // while the previous tab was open.
  const handleChangeFilterTab = (nextTab) => {
    setGoalCompletionSnapshot(
      allLinkedGoals.reduce((acc, goal) => {
        if (goal?.id) acc[goal.id] = Boolean(goal.completed)
        return acc
      }, {})
    )
    setStepCompletionSnapshot(
      PLAN_STEPS.reduce((acc, step) => {
        if (!NON_COMPLETABLE_STEP_KEYS.has(step.key)) acc[step.key] = Boolean(stepsCompleted[step.key])
        return acc
      }, {})
    )
    setFilterTab(nextTab)
  }

  /* ── Derived data ── */

  // Present the sample goal alongside real linked goals using the same shape.
  const sampleGoalItem = sampleGoal
    ? {
        id: SAMPLE_GOAL_ROADMAP_ID,
        name: sampleGoal.title,
        description: sampleGoal.description,
        origin: sampleGoal.origin,
        linked: true,
        completed: sampleGoal.complete,
        isSampleGoal: true,
        isBusy: sampleGoalLoading || sampleGoalSaving,
        tasks: sampleGoal.steps.map((step) => ({
          id: step.key,
          name: step.label,
          description: step.description,
          completed: step.complete,
        })),
      }
    : null

  const allLinkedGoals = sampleGoalItem ? [...linkedGoals, sampleGoalItem] : linkedGoals

  const isStepCompleteInSnapshot = (stepKey) =>
    NON_COMPLETABLE_STEP_KEYS.has(stepKey)
      ? false
      : (stepCompletionSnapshot[stepKey] ?? Boolean(stepsCompleted[stepKey]))

  const isGoalCompleteInSnapshot = (goal) =>
    goal?.id == null
      ? Boolean(goal?.completed)
      : (goalCompletionSnapshot[goal.id] ?? Boolean(goal.completed))

  // Flatten steps, assignments, and goals into one ordered list: assignments
  // first, then the Daily Quest, then linked goals, then the remaining steps.
  const allPlanItems = PLAN_STEPS.reduce((acc, step) => {
    if (step.key === "daily-quest") {
      curriculumAssignments.forEach((assignment, index) => {
        const { type, id } = getAssignmentTypeAndId(assignment)
        if (!id) return
        acc.push({
          type: "assignment",
          assignment,
          key: `${type}-${id}-${assignment?.due_at ?? index}`,
        })
      })
    }

    acc.push({ type: "step", step })

    if (step.key === "daily-quest") {
      allLinkedGoals.forEach((goal) => acc.push({ type: "goal", goal }))
    }

    return acc
  }, [])

  const isPlanItemCompleted = (item) => {
    if (item.type === "step") return isStepCompleteInSnapshot(item.step.key)
    if (item.type === "goal") return isGoalCompleteInSnapshot(item.goal)
    if (item.type === "assignment") return isAssignmentCompleted(item.assignment)
    return false
  }

  const hasCompletedPlanItems = allPlanItems.some(isPlanItemCompleted)

  const displayPlanItems = allPlanItems.filter((item) => {
    const isCompleted = isPlanItemCompleted(item)
    if (filterTab === "completed") return isCompleted

    // "Active" tab: assignments additionally need an active status.
    if (item.type === "assignment") {
      const status = getAssignmentStatus(item.assignment)
      return ACTIVE_ASSIGNMENT_STATUSES.has(status) || (!status && !isCompleted)
    }
    return !isCompleted
  })

  // If the last completed item was un-checked, the Completed tab is empty —
  // fall back to Active.
  useEffect(() => {
    if (!hasCompletedPlanItems && filterTab === "completed") {
      setFilterTab("active")
    }
  }, [hasCompletedPlanItems, filterTab])

  const goalCreatedDate = careerPlan?.createdAt
    ? formatDate(new Date(careerPlan.createdAt), { month: "long", year: "numeric" })
    : null

  /* ── Render ── */

  return (
    <div className="roadmap w-py-2">
      <div className="roadmap-main w-container w-mb-3">
        {/* Goal (left) */}
        <div className="roadmap-goal w-pl-3">
          <div className="roadmap-goal-inner w-flex w-flex-column">
            <WingsBadge variant="tal-e" className="w-mb-1">
              <FormattedMessage id="roadmap-your-goal" defaultMessage="My Future" />
            </WingsBadge>

            {careerPlan ? (
              <WingsHeading level={1} className="roadmap-goal-title w-mb-2">
                {careerPlan.title}
              </WingsHeading>
            ) : (
              <WingsDisplay ref={greetingRef} className="w-mb-2 roadmap-greeting">
                {locale === "es-ES"
                  ? `Hola, ${profile?.firstName ?? ""}!`
                  : `Hi, ${profile?.firstName ?? ""}!`}
              </WingsDisplay>
            )}

            <WingsText className="w-mb-2 w-container-sm">
              {careerPlan && goalCreatedDate ? (
                <FormattedMessage
                  id="roadmap-goal-description"
                  defaultMessage="You set this goal for yourself in <b>{date}</b>. Follow your Action Plan to earn points and level up as you work towards your goal."
                  values={{
                    date: goalCreatedDate,
                    b: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="roadmap-goal-description-empty"
                  defaultMessage="Follow your Action Plan to earn points and level up as you work towards your goal."
                />
              )}
            </WingsText>
          </div>
        </div>

        {/* Action Plan (right) */}
        <div className="roadmap-action-plan">
          {/* Purple timeline bar — absolute, spans full container height, does not scroll */}
          <div className="roadmap-timeline-marker" aria-hidden="true">
            <span className="roadmap-timeline-marker-line" />
            <span className="roadmap-timeline-marker-dot roadmap-timeline-marker-dot-bottom" />
          </div>

          {/* "Action Plan" label — does not scroll */}
          <div
            className="roadmap-action-plan-header"
            id="roadmap-action-plan-focus-target"
            tabIndex={-1}
            data-main-content-focus-start="true"
          >
            <span className="roadmap-action-plan-top-dot" aria-hidden="true" />
            <WingsEyebrow as="span">
              <FormattedMessage id="roadmap-action-plan" defaultMessage="Action Plan" />
            </WingsEyebrow>

            {hasCompletedPlanItems && (
              <div className="roadmap-filter-tabs goal-tracking-filters w-ml-auto" role="group">
                <button
                  className={`goal-tracking-filter-button${filterTab === "active" ? " goal-tracking-filter-button-active" : ""}`}
                  onClick={() => handleChangeFilterTab("active")}
                >
                  <FormattedMessage id="dashboard-filter-active" defaultMessage="Active" />
                </button>
                <span className="goal-tracking-filter-sep" aria-hidden="true">|</span>
                <button
                  className={`goal-tracking-filter-button${filterTab === "completed" ? " goal-tracking-filter-button-active" : ""}`}
                  onClick={() => handleChangeFilterTab("completed")}
                >
                  <FormattedMessage id="dashboard-filter-completed" defaultMessage="Completed" />
                </button>
              </div>
            )}
          </div>

          {/* Roadmap steps — only this area scrolls */}
          <div className="roadmap-action-plan-scroll">
            <WingsAccordion defaultOpenKey={PLAN_STEPS.find((step) => step.active)?.key}>
              {displayPlanItems.map((item, idx) => {
                const displayIndex = idx + 1

                if (item.type === "goal") {
                  return (
                    <GoalPlanItem
                      key={item.goal.id}
                      displayIndex={displayIndex}
                      goal={item.goal}
                      onToggleTask={handleToggleGoalTask}
                      onToggleComplete={handleToggleGoalComplete}
                    />
                  )
                }

                if (item.type === "assignment") {
                  return (
                    <AssignmentPlanItem
                      key={item.key}
                      displayIndex={displayIndex}
                      assignment={item.assignment}
                    />
                  )
                }

                const { step } = item
                const canComplete = !NON_COMPLETABLE_STEP_KEYS.has(step.key)

                return (
                  <StepPlanItem
                    key={step.key}
                    displayIndex={displayIndex}
                    step={step}
                    canComplete={canComplete}
                    isComplete={canComplete ? (stepsCompleted[step.key] ?? false) : false}
                    onToggleComplete={handleToggleStepComplete}
                  />
                )
              })}
            </WingsAccordion>
          </div>
        </div>
      </div>

      <RoadmapFeaturedStories />
    </div>
  )
}

export default Roadmap